import type { List } from '@gonfalon/immutable-shim/immutableShim';
import { fromJS, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableValidationResults } from '@gonfalon/legacy-form-validation/validationUtils';
import {
  isChecked,
  isLength,
  isNotEmpty,
  optional,
  predicateFor,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';

import {
  AZURE,
  BIGQUERY,
  CLICKHOUSE,
  DATABRICKS,
  KINESIS,
  MPARTICLE,
  PUBSUB,
  REDSHIFT,
  S3,
  SEGMENT,
  SNOWFLAKE,
  SNOWFLAKE_V2,
} from '#components/integrations/constants';
import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks } from '#utils/access/types';
import type { Environment } from '#utils/environments/records/Environment';
import { createEnvironment } from '#utils/environments/records/Environment';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';
import type { Project } from '#utils/projects/records/Project';
import { createProject } from '#utils/projects/records/Project';

import { type ContextToMParticleMap } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DestinationConfig<T extends Record<any> = any> = T & {
  validate?: () => ImmutableValidationResults;
};

// Mirrors the backend RedshiftDataExportCompletedArtifactsRep. Present only on
// Redshift destinations that finished /complete-setup. Stored as an ImmutableMap
// because createDestination runs the response through fromJS.
export type CompletedArtifactsMap = ImmutableMap<{
  sqlSetupScript: string;
  s3BucketName: string;
}>;

type DestinationProps = {
  _access: AccessChecks | null;
  _links: ImmutableMap<{
    self: Link;
  }>;
  _id: string;
  name: string;
  environment: Environment;
  project: Project;
  kind: string;
  config: DestinationConfig;
  on: boolean;
  version: number;
  privacyPolicyApproved: boolean;
  createdAt?: number;
  maintainerId?: string;
  completedArtifacts?: CompletedArtifactsMap;
};

export class Destination extends Record<DestinationProps>({
  _access: null,
  _links: Map(),
  _id: '',
  name: '',
  environment: createEnvironment(),
  project: createProject(),
  kind: '',
  config: Map(),
  on: true,
  version: 0,
  privacyPolicyApproved: false,
  createdAt: undefined,
  maintainerId: undefined,
  completedArtifacts: undefined,
}) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this.get('_access');

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  selfLink() {
    return this.getIn(['_links', 'self', 'href']);
  }

  validate() {
    const baseValidation = validateRecord(
      this,
      isChecked('privacyPolicyApproved'),
      optional(isNotEmpty)('name'),
      isLength(0, 100)('name'),
    );

    const envValidation = this.environment.validate();
    const projValidation = this.project.validate();

    if (!this.config?.validate) {
      return baseValidation.merge(
        envValidation.mapKeys((key: string) => `environment.${key}`),
        projValidation.mapKeys((key: string) => `project.${key}`),
      );
    }

    const configValidation = this.config?.validate();
    return baseValidation.merge(
      configValidation.mapKeys((key: string) => `config.${key}`),
      envValidation.mapKeys((key: string) => `environment.${key}`),
      projValidation.mapKeys((key: string) => `project.${key}`),
    );
  }

  toRep() {
    return fromJS(this.toJSON()).withMutations((map: Destination) => {
      map.delete('environment');
      map.delete('project');
    });
  }
}

type KinesisDestinationConfigProps = {
  region: string;
  roleArn: string;
  streamName: string;
};

class KinesisDestinationConfig extends Record<KinesisDestinationConfigProps>({
  region: '',
  roleArn: '',
  streamName: '',
}) {
  validate() {
    return validateRecord(this, isNotEmpty('region'), isNotEmpty('roleArn'), isNotEmpty('streamName'));
  }
}

type PubsubDestinationConfigProps = {
  topic: string;
  project: string;
};

class PubsubDestinationConfig extends Record<PubsubDestinationConfigProps>({
  topic: '',
  project: '',
}) {
  validate() {
    return validateRecord(this, isNotEmpty('topic'), isNotEmpty('project'));
  }
}

type MParticleDestinationConfigProps = {
  environment: string;
  apiKey: string;
  secret: string;
  userIdentity: string | undefined;
  anonymousUserIdentity: string | undefined;
  userIdentities: List<ContextToMParticleMap> | undefined;
};

class MParticleDestinationConfig extends Record<MParticleDestinationConfigProps>({
  environment: '',
  apiKey: '',
  secret: '',
  userIdentity: undefined,
  anonymousUserIdentity: undefined,
  userIdentities: undefined,
}) {
  validate() {
    const baseValidation = validateRecord(this, isNotEmpty('environment'), isNotEmpty('apiKey'), isNotEmpty('secret'));
    const contextValidation = validateRecord(this, isNotEmpty('userIdentities'));

    return baseValidation.merge(contextValidation.mapKeys((key: string) => key));
  }
}

type SegmentDestinationConfigProps = {
  writeKey: string;
  anonymousIDContextKind: string;
  userIDContextKind: string;
};

class SegmentDestinationConfig extends Record<SegmentDestinationConfigProps>({
  writeKey: '',
  anonymousIDContextKind: '',
  userIDContextKind: '',
}) {
  validate() {
    const baseValidation = validateRecord(this, isNotEmpty('writeKey'));

    const contextValidation = validateRecord(
      this,
      isNotEmpty('anonymousIDContextKind'),
      isNotEmpty('userIDContextKind'),
    );

    return baseValidation.merge(contextValidation.mapKeys((key: string) => key));
  }
}

type AzureDestinationConfigProps = {
  namespace: string;
  name: string;
  policyName: string;
  policyKey: string;
};

class AzureDestinationConfig extends Record<AzureDestinationConfigProps>({
  namespace: '',
  name: '',
  policyName: '',
  policyKey: '',
}) {
  validate() {
    return validateRecord(
      this,
      isNotEmpty('namespace'),
      isNotEmpty('name'),
      isNotEmpty('policyName'),
      isNotEmpty('policyKey'),
    );
  }
}

type SnowflakeDestinationConfigProps = {
  accountId: string;
};

class SnowflakeDestinationConfig extends Record<SnowflakeDestinationConfigProps>({
  accountId: '',
}) {
  validate() {
    return validateRecord(this, isNotEmpty('accountId'));
  }
}

type SnowflakeV2DestinationConfigProps = {
  snowflakeHostAddress: string;
  databaseName: string;
  warehouseName: string;
  roleName: string;
  schemaName: string;
  userName: string;
  includeNetworkPolicy: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public_key: string;
  publicKey: string;
  environment: Environment;
};

class SnowflakeV2DestinationConfig extends Record<SnowflakeV2DestinationConfigProps>({
  snowflakeHostAddress: '',
  databaseName: '',
  warehouseName: '',
  roleName: '',
  schemaName: '',
  userName: '',
  includeNetworkPolicy: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public_key: '',
  publicKey: '',
  environment: createEnvironment(),
}) {
  validate() {
    return validateRecord(this, isNotEmpty('snowflakeHostAddress'));
  }
}

// Databricks catalog and schema identifiers: alphanumeric and underscores only, for every
// metastore type. This mirrors the backend `databricksIdentifierRegex`
// (internal/api2/destinations/destinations_warehouse.go) and the full-page Zod schema, so
// client-side validation matches the server for accounts with the full-page flag off. Hyphens are
// rejected — names like `my-catalog` previously passed here but got a backend 400.
const isDatabricksIdentifierName = predicateFor(
  (v: string) => !!v && !/^[a-zA-Z0-9_]+$/.test(v),
  () => 'Must contain only letters, numbers, and underscores',
);

type DatabricksDestinationConfigProps = {
  host: string;
  schema: string;
  catalog: string;
  httpPath: string;
  accessToken: string;
  metastore: string;
  s3BucketName: string;
  s3BucketRegion: string;
  // The Hive bucket access id is persisted and returned by the backend under `s3AccessId`
  // (foundation DatabricksConfig), and both forms and the request payload use the same key.
  s3AccessId: string;
  bucketSecretKey: string;
};

class DatabricksDestinationConfig extends Record<DatabricksDestinationConfigProps>({
  host: '',
  schema: '',
  catalog: '',
  httpPath: '',
  accessToken: '',
  metastore: 'unity-catalog',
  s3BucketName: '',
  s3BucketRegion: '',
  s3AccessId: '',
  bucketSecretKey: '',
}) {
  validate() {
    const validators = [
      isNotEmpty('host'),
      isNotEmpty('schema'),
      optional(isDatabricksIdentifierName)('schema'),
      isNotEmpty('catalog'),
      optional(isDatabricksIdentifierName)('catalog'),
      isNotEmpty('httpPath'),
      isNotEmpty('accessToken'),
      isNotEmpty('metastore'),
    ];
    if (this.metastore === 'hive') {
      validators.push(isNotEmpty('s3BucketName'));
      validators.push(isNotEmpty('s3BucketRegion'));
      validators.push(isNotEmpty('s3AccessId'));
      validators.push(isNotEmpty('bucketSecretKey'));
    }
    return validateRecord(this, ...validators);
  }
}

type BigQueryDestinationConfigProps = {
  serviceAccountEmail: string;
  projectId: string;
  schema: string;
  gcsBucketName: string;
  gcsBucketRegion: string;
};

class BigQueryDestinationConfig extends Record<BigQueryDestinationConfigProps>({
  serviceAccountEmail: '',
  projectId: '',
  schema: '',
  gcsBucketName: '',
  gcsBucketRegion: '',
}) {
  validate() {
    return validateRecord(
      this,
      isNotEmpty('projectId'),
      isNotEmpty('gcsBucketRegion'),
      isNotEmpty('serviceAccountEmail'),
      isNotEmpty('schema'),
      isNotEmpty('gcsBucketName'),
    );
  }
}

type RedshiftDestinationConfigProps = {
  endpoint: string;
  cluster: string;
  s3BucketRegion: string;
  iamRoleArn: string;
  accountId: string;
  databaseName: string;
  schemaName: string;
  userName: string;
};

class RedshiftDestinationConfig extends Record<RedshiftDestinationConfigProps>({
  endpoint: '',
  cluster: '',
  s3BucketRegion: '',
  iamRoleArn: '',
  accountId: '',
  databaseName: '',
  schemaName: '',
  userName: '',
}) {
  validate() {
    return validateRecord(
      this,
      isNotEmpty('endpoint'),
      isNotEmpty('cluster'),
      isNotEmpty('s3BucketRegion'),
      isNotEmpty('iamRoleArn'),
      isNotEmpty('accountId'),
    );
  }
}

type ClickHouseDestinationConfigProps = {
  host: string;
  port: string;
  username: string;
  password: string;
  s3BucketRegion: string;
  s3IamRoleArn: string;
};

class ClickHouseDestinationConfig extends Record<ClickHouseDestinationConfigProps>({
  host: '',
  port: '9440',
  username: '',
  password: '',
  s3BucketRegion: '',
  s3IamRoleArn: '',
}) {
  validate() {
    return validateRecord(
      this,
      isNotEmpty('host'),
      isNotEmpty('port'),
      isNotEmpty('username'),
      isNotEmpty('password'),
      isNotEmpty('s3BucketRegion'),
      isNotEmpty('s3IamRoleArn'),
    );
  }
}

type S3DestinationConfigProps = {
  bucketName: string;
  bucketRegion: string;
  folder: string;
  fileFormat: string;
  iamRoleArn: string;
};

class S3DestinationConfig extends Record<S3DestinationConfigProps>({
  bucketName: '',
  bucketRegion: '',
  folder: '',
  fileFormat: 'parquet',
  iamRoleArn: '',
}) {
  validate() {
    return validateRecord(this, isNotEmpty('bucketName'), isNotEmpty('bucketRegion'), isNotEmpty('iamRoleArn'));
  }
}

export function createDestination(props: CreateFunctionInput<Destination> = {}) {
  const destination = props instanceof Destination ? props : new Destination(fromJS(props));
  return destination.update('config', (c) => {
    switch (destination.kind) {
      case KINESIS:
        return new KinesisDestinationConfig(c);
      case PUBSUB:
        return new PubsubDestinationConfig(c);
      case MPARTICLE:
        return new MParticleDestinationConfig(c);
      case SEGMENT:
        return new SegmentDestinationConfig(c);
      case AZURE:
        return new AzureDestinationConfig(c);
      case SNOWFLAKE:
        return new SnowflakeDestinationConfig(c);
      case SNOWFLAKE_V2:
        return new SnowflakeV2DestinationConfig(c);
      case DATABRICKS:
        return new DatabricksDestinationConfig(c);
      case BIGQUERY:
        return new BigQueryDestinationConfig(c);
      case REDSHIFT:
        return new RedshiftDestinationConfig(c);
      case CLICKHOUSE:
        return new ClickHouseDestinationConfig(c);
      case S3:
        return new S3DestinationConfig(c);
      default:
        return c;
    }
  });
}
